import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import './UzupelnijRaport.css';
import kpiAPI from '../../api/kpi';
import { hasKpiPermission } from '../../utils/permissions';

const PROJECT = 'MAN';

function emptyCell() {
  return { value: '' };
}

function emptyRow(cols = 1) {
  return Array.from({ length: cols }, () => emptyCell());
}

function emptyMatrix(rows = 1, cols = 1) {
  return Array.from({ length: rows }, () => emptyRow(cols));
}

function normalizeSheetData(data) {
  if (!data || !Array.isArray(data)) return emptyMatrix();
  return data.map((row) => {
    if (!Array.isArray(row)) return [emptyCell()];
    return row.map((cell) => {
      if (cell && typeof cell === 'object' && 'value' in cell) return cell;
      return { value: cell != null ? String(cell) : '' };
    });
  });
}

function generateId() {
  return `sheet_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

const DEFAULT_COL_WIDTH = 120;
const MIN_COL_WIDTH = 40;

const SHEET_POWYZEJ24H = 'POWYŻEJ24H';
const COL_DATA_WPROWADZENIA = 'DATA WPROWADZENIA DANYCH';

function getHeaderValue(cell) {
  if (cell == null) return '';
  const v = cell && typeof cell === 'object' && 'value' in cell ? cell.value : cell;
  return v != null ? String(v).trim() : '';
}

function getColumnIndexByHeader(headerRow, name) {
  const needle = String(name || '').trim().toLowerCase();
  for (let i = 0; i < (headerRow?.length ?? 0); i++) {
    const h = getHeaderValue(headerRow[i]);
    if (h.toLowerCase() === needle) return i;
  }
  return -1;
}

function hasRowContent(row) {
  if (!row || !Array.isArray(row)) return false;
  return row.some((c) => (c?.value ?? '').toString().trim() !== '');
}

function findLastFilledRowIndex(matrix) {
  if (!matrix || matrix.length < 2) return 1;
  for (let r = matrix.length - 1; r >= 1; r--) {
    if (hasRowContent(matrix[r])) return r;
  }
  return 1;
}

function rectFromTwo(r1, c1, r2, c2) {
  return {
    r1: Math.min(r1, r2),
    c1: Math.min(c1, c2),
    r2: Math.max(r1, r2),
    c2: Math.max(c1, c2),
  };
}

function isInRect(row, col, sel) {
  if (!sel) return false;
  return row >= sel.r1 && row <= sel.r2 && col >= sel.c1 && col <= sel.c2;
}

function copyToClipboardFallback(text) {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } catch (e) {}
  document.body.removeChild(ta);
}

function FallbackSheet({ data, onChange, columnWidths, onColumnWidthsChange, sheetName, sheetId }) {
  const matrix = Array.isArray(data) && data.length > 0 ? data : emptyMatrix(3, 3);
  const tableRef = useRef(null);
  const scrollWrapRef = useRef(null);
  const numRows = matrix.length;
  const numCols = matrix[0]?.length ?? 0;

  const [selection, setSelection] = useState(() => rectFromTwo(1, 0, 1, 0));
  const [anchor, setAnchor] = useState({ row: 1, col: 0 });
  const anchorRef = useRef(anchor);
  anchorRef.current = anchor;
  const selectionRef = useRef(selection);
  selectionRef.current = selection;

  useEffect(() => {
    if (numRows === 0 || numCols === 0) return;
    setSelection((prev) => {
      const r1 = Math.max(0, Math.min(prev.r1, numRows - 1));
      const r2 = Math.max(0, Math.min(prev.r2, numRows - 1));
      const c1 = Math.max(0, Math.min(prev.c1, numCols - 1));
      const c2 = Math.max(0, Math.min(prev.c2, numCols - 1));
      return rectFromTwo(r1, c1, r2, c2);
    });
  }, [numRows, numCols, sheetId]);

  useEffect(() => {
    if (!tableRef.current || !scrollWrapRef.current) return;
    try {
      const lastRow = findLastFilledRowIndex(matrix);
      const tbody = tableRef.current.querySelector('tbody');
      if (!tbody) return;
      const trs = tbody.querySelectorAll('tr');
      const trIndex = lastRow - 1;
      if (trs[trIndex]) {
        const row = trs[trIndex];
        requestAnimationFrame(() => {
          try {
            row.scrollIntoView({ block: 'end', behavior: 'auto' });
          } catch (e) {
            // scrollIntoView może rzucać w rzadkich przypadkach (np. element odłączony)
          }
        });
      }
    } catch (e) {
      // Zapobiega crashowi aplikacji przy błędzie w logice przewijania
    }
  }, [sheetId]);

  const widths =
    Array.isArray(columnWidths) && columnWidths.length === numCols
      ? columnWidths
      : Array.from({ length: numCols }, () => DEFAULT_COL_WIDTH);

  const [resizing, setResizing] = useState({ col: -1, startX: 0, startWidth: 0 });
  const widthsRef = useRef(widths);
  widthsRef.current = widths;

  useEffect(() => {
    if (resizing.col < 0) return;
    const onMove = (e) => {
      const delta = e.clientX - resizing.startX;
      const newWidth = Math.max(MIN_COL_WIDTH, resizing.startWidth + delta);
      const current = widthsRef.current;
      const next = [...current];
      next[resizing.col] = newWidth;
      onColumnWidthsChange?.(next);
    };
    const onUp = () => setResizing({ col: -1, startX: 0, startWidth: 0 });
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing.col, resizing.startX, resizing.startWidth, onColumnWidthsChange]);

  const handleResizeStart = (col, e) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing({ col, startX: e.clientX, startWidth: widths[col] ?? DEFAULT_COL_WIDTH });
  };

  const handleCellChange = (rowIdx, colIdx, value) => {
    const next = matrix.map((row, r) =>
      r === rowIdx
        ? row.map((cell, c) => (c === colIdx ? { ...cell, value } : cell))
        : row
    );
    onChange(next);
  };

  const focusCell = (row, col) => {
    setTimeout(() => {
      const el = tableRef.current?.querySelector(
        `input[data-row="${row}"][data-col="${col}"]`
      );
      if (el) {
        el.focus();
      }
    }, 0);
  };

  const dragRef = useRef({ active: false, startRow: 0, startCol: 0 });

  const handleCellMouseDown = (e, row, col) => {
    if (e.target.closest('.uzupelnij-raport-col-resize-handle')) return;
    const rect = rectFromTwo(row, col, row, col);
    if (e.shiftKey) {
      const a = anchorRef.current;
      setSelection(rectFromTwo(a.row, a.col, row, col));
    } else {
      setAnchor({ row, col });
      setSelection(rect);
      dragRef.current = { active: true, startRow: row, startCol: col };
    }
  };

  const handleCellFocus = (row, col) => {
    if (dragRef.current.active) return;
    setAnchor({ row, col });
    setSelection(rectFromTwo(row, col, row, col));
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!dragRef.current.active) return;
      const cell = e.target?.closest?.('td[data-row][data-col], th[data-row][data-col]');
      if (!cell) return;
      const r = parseInt(cell.getAttribute('data-row'), 10);
      const c = parseInt(cell.getAttribute('data-col'), 10);
      if (Number.isNaN(r) || Number.isNaN(c)) return;
      const start = dragRef.current;
      setSelection(rectFromTwo(start.startRow, start.startCol, r, c));
    };
    const handleMouseUp = () => {
      dragRef.current = { active: false, startRow: 0, startCol: 0 };
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleKeyDown = (e, row, col) => {
    if (e.key === 'Delete' || e.key === 'Backspace') {
      const sel = selectionRef.current;
      if (sel) {
        e.preventDefault();
        e.stopPropagation();
        const next = matrix.map((r, ri) =>
          r.map((cell, ci) => {
            if (ri >= sel.r1 && ri <= sel.r2 && ci >= sel.c1 && ci <= sel.c2) {
              return { ...cell, value: '' };
            }
            return cell;
          })
        );
        onChange(next);
      }
      return;
    }
    if (e.key === 'c' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      const sel = selectionRef.current;
      if (!sel) return;
      const lines = [];
      for (let r = sel.r1; r <= sel.r2; r++) {
        const rowCells = [];
        for (let c = sel.c1; c <= sel.c2; c++) {
          const cell = matrix[r]?.[c];
          const v = (cell?.value ?? '').toString();
          rowCells.push(v);
        }
        lines.push(rowCells.join('\t'));
      }
      const text = lines.join('\n');
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).catch(() => {
          copyToClipboardFallback(text);
        });
      } else {
        copyToClipboardFallback(text);
      }
      return;
    }
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      let nr = row;
      let nc = col;
      if (e.key === 'ArrowRight') nc = Math.min(col + 1, numCols - 1);
      else if (e.key === 'ArrowLeft') nc = Math.max(col - 1, 0);
      else if (e.key === 'ArrowDown') nr = Math.min(row + 1, numRows - 1);
      else if (e.key === 'ArrowUp') nr = Math.max(row - 1, 0);
      if (e.shiftKey) {
        const a = anchorRef.current;
        setSelection(rectFromTwo(a.row, a.col, nr, nc));
      } else {
        setAnchor({ row: nr, col: nc });
        setSelection(rectFromTwo(nr, nc, nr, nc));
      }
      focusCell(nr, nc);
    }
  };

  /** Wklejanie z Excela: text/plain z tabami (kolumny) i enterami (wiersze) → rozłożenie do komórek od aktywnej. */
  const handlePaste = (e) => {
    const text = e.clipboardData?.getData('text/plain');
    if (!text || !tableRef.current) return;
    const active = document.activeElement;
    if (!active || !tableRef.current.contains(active)) return;
    const startRow = parseInt(active.getAttribute('data-row'), 10);
    const startCol = parseInt(active.getAttribute('data-col'), 10);
    if (Number.isNaN(startRow) || Number.isNaN(startCol)) return;

    const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '' || line.includes('\t'));
    const grid = lines.map((line) => line.split('\t'));
    if (grid.length === 0) return;

    const pasteRows = grid.length;
    const pasteCols = Math.max(0, ...grid.map((row) => row.length));
    if (pasteCols === 0) return;

    const needRows = startRow + pasteRows;
    const needCols = startCol + pasteCols;
    let next = matrix.map((row) => row.map((cell) => ({ ...cell })));

    if (needRows > numRows) {
      for (let r = numRows; r < needRows; r++) {
        next.push(emptyRow(numCols));
      }
    }
    if (needCols > numCols) {
      const addCols = needCols - numCols;
      next = next.map((row) => [...row, ...emptyRow(addCols)]);
    }

    for (let r = 0; r < pasteRows; r++) {
      for (let c = 0; c < pasteCols; c++) {
        const tr = startRow + r;
        const tc = startCol + c;
        const val = grid[r][c] != null ? String(grid[r][c]).trimEnd() : '';
        if (next[tr] && next[tr][tc]) next[tr][tc] = { ...next[tr][tc], value: val };
      }
    }

    const isPowyzej24h = String(sheetName || '').trim().toLowerCase() === SHEET_POWYZEJ24H.toLowerCase();
    if (isPowyzej24h && next[0]) {
      const dataWprowadzeniaCol = getColumnIndexByHeader(next[0], COL_DATA_WPROWADZENIA);
      if (dataWprowadzeniaCol >= 0 && next[startRow] && next[startRow][dataWprowadzeniaCol]) {
        const today = new Date();
        const d = String(today.getDate()).padStart(2, '0');
        const m = String(today.getMonth() + 1).padStart(2, '0');
        const y = today.getFullYear();
        const ymd = `${d}.${m}.${y}`;
        next[startRow][dataWprowadzeniaCol] = { ...next[startRow][dataWprowadzeniaCol], value: ymd };
      }
    }

    onChange(next);
    e.preventDefault();
    e.stopPropagation();
  };

  const headerRow = matrix[0];
  const bodyRows = matrix.slice(1);
  const tableWidthPx = widths.reduce((sum, w) => sum + w, 0);

  return (
    <div
      ref={scrollWrapRef}
      className={`uzupelnij-raport-fallback-sheet${resizing.col >= 0 ? ' uzupelnij-raport-resizing' : ''}`}
      onPaste={handlePaste}
    >
      <table
        ref={tableRef}
        className="uzupelnij-raport-fallback-table"
        style={{ width: tableWidthPx, minWidth: tableWidthPx }}
      >
        <colgroup>
          {widths.map((w, i) => (
            <col key={i} style={{ width: w, minWidth: w }} />
          ))}
        </colgroup>
        <thead>
          <tr className="uzupelnij-raport-header-row">
            {headerRow?.map((cell, c) => (
              <th
                key={c}
                data-row={0}
                data-col={c}
                className={`uzupelnij-raport-header-cell ${isInRect(0, c, selection) ? 'uzupelnij-raport-cell-selected' : ''}`}
                onMouseDown={(e) => handleCellMouseDown(e, 0, c)}
              >
                <div className="uzupelnij-raport-header-cell-wrap">
                  <input
                    type="text"
                    data-row={0}
                    data-col={c}
                    value={cell?.value ?? ''}
                    onChange={(e) => handleCellChange(0, c, e.target.value)}
                    onFocus={() => handleCellFocus(0, c)}
                    onKeyDown={(e) => handleKeyDown(e, 0, c)}
                    className="uzupelnij-raport-fallback-input uzupelnij-raport-header-input"
                    placeholder={`Kolumna ${String.fromCharCode(65 + c)}`}
                    title="Kliknij i wpisz, aby zmienić nazwę nagłówka"
                  />
                </div>
                <div
                  className="uzupelnij-raport-col-resize-handle"
                  onMouseDown={(e) => handleResizeStart(c, e)}
                  title="Przeciągnij, aby zmienić szerokość kolumny"
                  role="separator"
                  aria-orientation="vertical"
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td
                  key={c}
                  data-row={r + 1}
                  data-col={c}
                  className={isInRect(r + 1, c, selection) ? 'uzupelnij-raport-cell-selected' : ''}
                  onMouseDown={(e) => handleCellMouseDown(e, r + 1, c)}
                >
                  <input
                    type="text"
                    data-row={r + 1}
                    data-col={c}
                    value={cell?.value ?? ''}
                    onChange={(e) => handleCellChange(r + 1, c, e.target.value)}
                    onFocus={() => handleCellFocus(r + 1, c)}
                    onKeyDown={(e) => handleKeyDown(e, r + 1, c)}
                    className="uzupelnij-raport-fallback-input"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function defaultColumnWidths(numCols) {
  return Array.from({ length: numCols }, () => DEFAULT_COL_WIDTH);
}

const INITIAL_SHEET = {
  id: 'initial',
  name: 'Arkusz 1',
  data: emptyMatrix(5, 5),
  columnWidths: defaultColumnWidths(5),
};

function UzupelnijRaport({ user }) {
  const [sheets, setSheets] = useState([INITIAL_SHEET]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [SpreadsheetComponent, setSpreadsheetComponent] = useState(null);
  const saveTimeoutRef = useRef(null);
  const sheetsRef = useRef(sheets);
  sheetsRef.current = sheets;

  useEffect(() => {
    import('react-spreadsheet')
      .then((m) => setSpreadsheetComponent(() => m.default))
      .catch((err) => {
        console.warn('react-spreadsheet nie załadowany – używam fallback tabeli.', err);
      });
  }, []);

  const loadReports = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await kpiAPI.getReports(PROJECT);
      const list = Array.isArray(res?.sheets) ? res.sheets : [];
      const nextSheets =
        list.length > 0
          ? list.map((s) => {
              const data = normalizeSheetData(s.data);
              const numCols = data[0]?.length ?? 0;
              const rawWidths = Array.isArray(s.columnWidths) ? s.columnWidths : [];
              const columnWidths = [];
              for (let i = 0; i < numCols; i++) {
                columnWidths.push(typeof rawWidths[i] === 'number' && rawWidths[i] >= MIN_COL_WIDTH ? rawWidths[i] : DEFAULT_COL_WIDTH);
              }
              return {
                id: s.id || generateId(),
                name: s.name || 'Arkusz',
                data,
                columnWidths,
              };
            })
          : [INITIAL_SHEET];
      setSheets(nextSheets);
      setActiveIndex(0);
    } catch (err) {
      console.error('Błąd ładowania raportu:', err);
      setError('Nie udało się załadować raportu (dane lokalne).');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const saveReports = useCallback(
    async (nextSheets) => {
      const payload = {
        sheets: nextSheets.map((s) => ({
          id: s.id,
          name: s.name,
          data: s.data,
          columnWidths: Array.isArray(s.columnWidths) ? s.columnWidths : undefined,
        })),
      };
      setSaving(true);
      setError(null);
      try {
        await kpiAPI.saveReports(PROJECT, payload);
      } catch (err) {
        console.error('Błąd zapisu raportu:', err);
        setError('Nie udało się zapisać raportu.');
      } finally {
        setSaving(false);
      }
    },
    []
  );

  const scheduleSave = useCallback(
    (nextSheets) => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        saveReports(nextSheets);
        saveTimeoutRef.current = null;
      }, 500);
    },
    [saveReports]
  );

  /** Zapis natychmiastowy (np. przy dodawaniu kolumny/wiersza), żeby dane nie zniknęły po odświeżeniu */
  const saveImmediate = useCallback(
    (nextSheets) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      saveReports(nextSheets);
    },
    [saveReports]
  );

  useEffect(() => {
    const flushSave = () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
        saveReports(sheetsRef.current);
      }
    };
    window.addEventListener('beforeunload', flushSave);
    return () => window.removeEventListener('beforeunload', flushSave);
  }, [saveReports]);

  const updateSheetData = useCallback(
    (index, newData) => {
      setSheets((prev) => {
        const next = prev.map((s, i) =>
          i === index ? { ...s, data: newData } : s
        );
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave]
  );

  const updateSheetColumnWidths = useCallback(
    (index, newWidths) => {
      setSheets((prev) => {
        const next = prev.map((s, i) =>
          i === index ? { ...s, columnWidths: newWidths } : s
        );
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave]
  );

  const addSheet = useCallback(() => {
    const name = `Arkusz ${sheets.length + 1}`;
    const newSheet = { id: generateId(), name, data: emptyMatrix(5, 5), columnWidths: defaultColumnWidths(5) };
    setSheets((prev) => {
      const next = [...prev, newSheet];
      saveImmediate(next);
      return next;
    });
    setActiveIndex(sheets.length);
  }, [sheets.length, saveImmediate]);

  const renameSheet = useCallback((index, newName) => {
    const trimmed = (newName || '').trim() || `Arkusz ${index + 1}`;
    setSheets((prev) => {
      const next = prev.map((s, i) =>
        i === index ? { ...s, name: trimmed } : s
      );
      saveImmediate(next);
      return next;
    });
  }, [saveImmediate]);

  const removeSheet = useCallback(
    (index) => {
      if (sheets.length <= 1) return;
      if (!window.confirm('Czy na pewno chcesz usunąć ten arkusz?')) return;
      setSheets((prev) => {
        const next = prev.filter((_, i) => i !== index);
        saveImmediate(next);
        return next;
      });
      setActiveIndex((i) => (i >= index && i > 0 ? i - 1 : i));
    },
    [sheets.length, saveImmediate]
  );

  const addColumn = useCallback(() => {
    if (sheets[activeIndex] == null) return;
    const newCol = emptyRow(1);
    setSheets((prev) => {
      const next = prev.map((s, i) =>
        i === activeIndex
          ? {
              ...s,
              data: s.data.map((row) => [...row, newCol[0] || emptyCell()]),
              columnWidths: [...(s.columnWidths || defaultColumnWidths(s.data[0]?.length ?? 0)), DEFAULT_COL_WIDTH],
            }
          : s
      );
      saveImmediate(next);
      return next;
    });
  }, [activeIndex, sheets, saveImmediate]);

  const addRow = useCallback(() => {
    if (sheets[activeIndex] == null) return;
    const sheet = sheets[activeIndex];
    const cols = sheet.data[0]?.length || 1;
    const rowsToAdd = 10;
    setSheets((prev) => {
      const next = prev.map((s, i) =>
        i === activeIndex
          ? { ...s, data: [...s.data, ...Array.from({ length: rowsToAdd }, () => emptyRow(cols))] }
          : s
      );
      saveImmediate(next);
      return next;
    });
  }, [activeIndex, sheets, saveImmediate]);

  const removeColumn = useCallback(() => {
    if (sheets[activeIndex] == null) return;
    const sheet = sheets[activeIndex];
    const colCount = sheet.data[0]?.length ?? 0;
    if (colCount <= 1) return;
    setSheets((prev) => {
      const next = prev.map((s, i) =>
        i === activeIndex
          ? {
              ...s,
              data: s.data.map((row) => row.slice(0, -1)),
              columnWidths: (s.columnWidths || defaultColumnWidths(colCount)).slice(0, -1),
            }
          : s
      );
      saveImmediate(next);
      return next;
    });
  }, [activeIndex, sheets, saveImmediate]);

  const removeRow = useCallback(() => {
    if (sheets[activeIndex] == null) return;
    const sheet = sheets[activeIndex];
    if (sheet.data.length <= 1) return;
    setSheets((prev) => {
      const next = prev.map((s, i) =>
        i === activeIndex
          ? { ...s, data: s.data.slice(0, -1) }
          : s
      );
      saveImmediate(next);
      return next;
    });
  }, [activeIndex, sheets, saveImmediate]);

  const currentSheet = sheets[activeIndex];

  const canDeleteColumnsRows = hasKpiPermission(user, 'uzupelnijRaportDeleteColumnsRows');
  const canEditSheetNames = hasKpiPermission(user, 'uzupelnijRaportEditSheetNames');
  const canManageSheets = hasKpiPermission(user, 'uzupelnijRaportManageSheets');
  const canExportExcel = hasKpiPermission(user, 'uzupelnijRaportExportExcel');

  const exportSheetToExcel = useCallback((sheet) => {
    if (!sheet?.data?.length) return;
    const aoa = sheet.data.map((row) => row.map((cell) => (cell?.value ?? '').toString()));
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    const safeName = (sheet.name || 'Arkusz').replace(/[\[\]:*?/\\]/g, '_').slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, safeName);
    const fileName = `${safeName}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }, []);

  const handleSpreadsheetChange = useCallback(
    (newData) => {
      updateSheetData(activeIndex, newData);
    },
    [activeIndex, updateSheetData]
  );

  const renderSheet = () => {
    if (!currentSheet) return null;
    const widths =
      Array.isArray(currentSheet.columnWidths) && currentSheet.columnWidths.length === (currentSheet.data[0]?.length ?? 0)
        ? currentSheet.columnWidths
        : defaultColumnWidths(currentSheet.data[0]?.length ?? 0);
    return (
      <FallbackSheet
        data={currentSheet.data}
        onChange={(newData) => updateSheetData(activeIndex, newData)}
        columnWidths={widths}
        onColumnWidthsChange={(w) => updateSheetColumnWidths(activeIndex, w)}
        sheetName={currentSheet.name}
        sheetId={currentSheet.id}
      />
    );
  };

  return (
    <div className="uzupelnij-raport-container">
      <div className="uzupelnij-raport-toolbar">
        <div className="uzupelnij-raport-actions uzupelnij-raport-actions-left">
          <button type="button" className="uzupelnij-raport-btn" onClick={addColumn} title="Dodaj kolumnę">
            + Kolumna
          </button>
          <button type="button" className="uzupelnij-raport-btn" onClick={addRow} title="Dodaj 10 wierszy">
            + Wiersz
          </button>
          <button
            type="button"
            className="uzupelnij-raport-btn uzupelnij-raport-btn-save"
            onClick={() => saveImmediate(sheets)}
            title="Zapisz raport teraz"
          >
            Zapisz teraz
          </button>
          {canExportExcel && currentSheet && (
            <button
              type="button"
              className="uzupelnij-raport-btn"
              onClick={() => exportSheetToExcel(currentSheet)}
              title="Pobierz bieżący arkusz do Excel"
            >
              ↓ Excel
            </button>
          )}
          {saving && <span className="uzupelnij-raport-saving">Zapisywanie…</span>}
          {error && <span className="uzupelnij-raport-error">{error}</span>}
        </div>
        {canDeleteColumnsRows && (
          <div className="uzupelnij-raport-actions uzupelnij-raport-actions-right">
            <button type="button" className="uzupelnij-raport-btn uzupelnij-raport-btn-remove" onClick={removeColumn} title="Usuń ostatnią kolumnę">
              − Kolumna
            </button>
            <button type="button" className="uzupelnij-raport-btn uzupelnij-raport-btn-remove" onClick={removeRow} title="Usuń ostatni wiersz">
              − Wiersz
            </button>
          </div>
        )}
      </div>
      <div className="uzupelnij-raport-sheet-wrap">
        {loading ? (
          <div className="uzupelnij-raport-loading">Ładowanie danych…</div>
        ) : (
          currentSheet && renderSheet()
        )}
      </div>
      <div className="uzupelnij-raport-tabs-bar">
        <div className="uzupelnij-raport-tabs">
          {sheets.map((sheet, i) => (
            <div
              key={sheet.id}
              className={`uzupelnij-raport-tab ${i === activeIndex ? 'active' : ''}`}
              onClick={() => setActiveIndex(i)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveIndex(i); }}
              aria-label={`Arkusz ${sheet.name}`}
            >
              <span className="uzupelnij-raport-tab-label">{sheet.name}</span>
              <div className="uzupelnij-raport-tab-actions">
                {canEditSheetNames && (
                  <button
                    type="button"
                    className="uzupelnij-raport-tab-rename"
                    onClick={(e) => {
                      e.stopPropagation();
                      const name = window.prompt('Nazwa arkusza:', sheet.name);
                      if (name != null) renameSheet(i, name);
                    }}
                    title="Zmień nazwę"
                  >
                    ✎
                  </button>
                )}
                {canManageSheets && sheets.length > 1 && (
                  <button
                    type="button"
                    className="uzupelnij-raport-tab-close"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSheet(i);
                    }}
                    title="Usuń arkusz"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          ))}
          {canManageSheets && (
            <button
              type="button"
              className="uzupelnij-raport-add-sheet"
              onClick={addSheet}
              title="Dodaj arkusz"
            >
              + Arkusz
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default UzupelnijRaport;
